// Display · CopyableValue
//
// Canonical copy-affordance wrapper. Renders a text value with an
// inline icon-only copy button. Hover-only mode is supported.
// Uses App.useApp() so the success toast flows through the AntD context
// (never import `message` directly — project rule).
//
// Composite — auto-emits `copy-button` testid on the inner <button>.
// Ships a sibling `CopyableValue.e2e.ts` Playwright locator.
//
// All visual values come from `@ds/tokens/palette`.

import React from "react";
import { App, Tooltip } from "antd";
import { CheckOutlined, CopyOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { formatCopyableDisplay, type CopyableFormat } from "@ds/display/CopyableValue.helpers";
import {
  FONT_SIZE,
  LINE_HEIGHT,
  MOTION,
  TEXT,
  TONE,
} from "@ds/tokens/palette";
import { Inline } from "@ds/layout/Inline";

export type { CopyableFormat } from "@ds/display/CopyableValue.helpers";
import { TextValue } from "@ds/display/TextValue";

// ─── Hover CSS (injected once, idempotent) ────────────────────────────────────
//
// hoverOnly mode shows the copy icon only on hover. Rather than tracking hover
// in React state (which causes re-renders on every mouse move), we inject a
// scoped CSS rule that drives visibility via the :hover pseudo-class.
// Same idempotent-injection pattern used by @ds/editable/Field and Breadcrumb.

const COPYABLE_CSS_ID = "__ds-copyable-hover";

function ensureCopyableCSS(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(COPYABLE_CSS_ID)) return;
  const el = document.createElement("style");
  el.id = COPYABLE_CSS_ID;
  el.textContent = [
    `.ds-copyable__btn {
      background: transparent;
      border: 0;
      padding: 0;
      cursor: pointer;
      color: ${TEXT.muted};
      font-size: ${FONT_SIZE.label}px;
      line-height: ${LINE_HEIGHT.none};
      transition: color ${MOTION.fast} ease;
      display: inline-flex;
      align-items: center;
    }`,
    `.ds-copyable__btn--copied { color: ${TONE.green.base}; }`,
    `.ds-copyable--hover-only .ds-copyable__btn { opacity: 0; transition: opacity ${MOTION.fast} ease, color ${MOTION.fast} ease; }`,
    `.ds-copyable--hover-only:hover .ds-copyable__btn { opacity: 1; }`,
    `.ds-copyable--hover-only .ds-copyable__btn--copied { opacity: 1; }`,
  ].join("\n");
  document.head.appendChild(el);
}

// ─── Inputs (data props — the model) ─────────────────────────────────────

export interface CopyableValueInput {
  /** Text value to display and copy. Null/undefined renders `emptyPlaceholder`. */
  value: string | null | undefined;
  /** Formatting hint — drives monospace font and optional display transforms. */
  format?: CopyableFormat;
  /** Override the copied value (e.g. copy raw IBAN while displaying a grouped one). */
  copyValue?: string;
  /** Show the copy icon only on hover (default: always visible). */
  hoverOnly?: boolean;
  /** Content rendered when value is null/empty. Defaults to an em-dash. */
  emptyPlaceholder?: React.ReactNode;
  /** `data-testid` applied to the root wrapper span. Inner `copy-button` is auto-emitted. */
  testId?: string;
}

// ─── Outputs (events emitted — controller hooks) ─────────────────────────

export interface CopyableValueOutput {
  /** Fires after a successful copy-to-clipboard operation. */
  onCopied?: (value: string) => void;
}

// ─── Component contract ──────────────────────────────────────────────────

export type CopyableValueProps = CopyableValueInput & CopyableValueOutput;

// ─── Helpers ─────────────────────────────────────────────────────────────

/** Returns the value to write to the clipboard. */
function resolveCopyValue(
  value: string,
  format: CopyableFormat,
  copyValue: string | undefined,
): string {
  if (copyValue !== undefined) return copyValue;
  // NIP: always copy normalised raw digits so downstream systems get a
  // canonical value regardless of how it was stored.
  if (format === "nip") return formatCopyableDisplay(value, "nip");
  return value;
}

// ─── Implementation ──────────────────────────────────────────────────────

export const CopyableValue: React.FC<CopyableValueProps> = ({
  value,
  format = "plain",
  copyValue,
  hoverOnly,
  emptyPlaceholder,
  testId,
  onCopied,
}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [copied, setCopied] = React.useState(false);

  ensureCopyableCSS();

  if (value == null || value === "") {
    return (
      <>
        {emptyPlaceholder ?? (
          <TextValue as="span" tone="faint">—</TextValue>
        )}
      </>
    );
  }

  const display = formatCopyableDisplay(value, format);
  const toCopy  = resolveCopyValue(value, format, copyValue);

  const copy = async (e: React.SyntheticEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(toCopy);
      setCopied(true);
      message.success(t("common.card.copied", "Skopiowano"));
      onCopied?.(toCopy);
      window.setTimeout(() => setCopied(false), 800);
    } catch {
      message.error(t("common.card.copy_failed", "Nie udało się skopiować"));
    }
  };

  return (
    <Inline
      as="span"
      inline
      gap="xs"
      align="center"
      testId={testId}
      className={hoverOnly ? "ds-copyable--hover-only" : undefined}
    >
      <TextValue
        as="span"
        wrapAnywhere={format === "iban"}
      >
        {display}
      </TextValue>
      <Tooltip title={t("common.card.copy", "Kopiuj")}>
        <button
          type="button"
          data-testid="copy-button"
          className={`ds-copyable__btn${copied ? " ds-copyable__btn--copied" : ""}`}
          onClick={copy}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              copy(e);
            }
          }}
        >
          {copied ? <CheckOutlined /> : <CopyOutlined />}
        </button>
      </Tooltip>
    </Inline>
  );
};
